<template>
  <GenericUploadTable
    v-model="localImperialActive"
    :title="title"
    toggleLabel="Custom Fluid Properties"
    :tableData="wellProperties?.custom_fluid_properties || []"
    :columns="displayedColumns"
    :onFileSelected="handleFileSelected"
    :onClear="removeData"
    :fileInputKey="fileInputKeyCorrelations"
    accept=".xlsx, .xls"
    uploadLabel="Add Fluid Properties Sheet"
    uploadIcon="fas fa-plus-circle"
    :maxFileSize="5000000"
    templateDownloadUrl="/Imperial.Fluid.Properties.template.xlsx"
    templateDownloadName="Imperial.Fluid.Properties.template.xlsx"
    templateLinkText="Fluid Properties Template"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import GenericUploadTable from "@/views/scenarios/wellInfo/GenericUploadTable.vue";

import { useToast } from "primevue/usetoast";
import { read, utils } from "xlsx";
import { injectStrict } from "@/utils/utils";

import { wellPropertiesFormKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { RegressionCoefficientsMap } from "@/composables/scenarioCalculations/linearizationAlgorithm";
import { FluidPropertyField } from "@/composables/scenarioCalculations/linearizationAlgorithm";
import { PoundsPerSquareInchAbsolute } from "@/libs/units/pressure";
import { Fahrenheit } from "@/libs/units/temperature";
import { BarrelPerStockTankBarrel } from "@/libs/units/generic";
import { StandardCubicFootPerStockTankBarrel } from "@/libs/units/liquidRatio";
import {
  VISCOSITY_PRESSURE_TYPE,
  VISCOSITY_TEMPERATURE_TYPE
} from "@/libs/calculations/constants";

const title = "Custom Fluid Properties";
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const toaster = useToast();
const localImperialActive = ref(false);
const fileInputKeyCorrelations = ref(0);
const wellProperties = computed(() => wellPropertiesForm.data.value);

interface FluidPropertiesRow {
  [FluidPropertyField.controllingValue]: number;
  [FluidPropertyField.controlType]:
    | typeof VISCOSITY_TEMPERATURE_TYPE
    | typeof VISCOSITY_PRESSURE_TYPE;
  [FluidPropertyField.bubblePointPressurePSIA]: number | null;
  [FluidPropertyField.oilFormationVolumeFactorBBLSTB]: number | null;
  [FluidPropertyField.solutionGasOilRatioSCFSTB]: number | null;
  [FluidPropertyField.waterFormationVolumeFactorBBLSTB]: number | null;
  [FluidPropertyField.solutionGasWaterRatioSCFSTB]: number | null;
  [FluidPropertyField.gasFormationVolumeFactorRCFSCF]: number | null;
}

function toNumberOrNull(val: unknown): number | null {
  if (val === null || val === "" || val === undefined) {
    return null;
  }
  return Number(val);
}
const allColumns = [
  { field: FluidPropertyField.controllingValue, header: "Controlling Value" },
  { field: FluidPropertyField.controlType, header: "Control Type" },
  {
    field: FluidPropertyField.bubblePointPressurePSIA,
    header: "Bubble Point Pressure"
  },
  {
    field: FluidPropertyField.oilFormationVolumeFactorBBLSTB,
    header: "Oil FVF"
  },
  {
    field: FluidPropertyField.solutionGasOilRatioSCFSTB,
    header: "Solution Gas Oil Ratio"
  },
  {
    field: FluidPropertyField.waterFormationVolumeFactorBBLSTB,
    header: "Water FVF"
  },
  {
    field: FluidPropertyField.solutionGasWaterRatioSCFSTB,
    header: "Solution Gas Water Ratio"
  },
  {
    field: FluidPropertyField.gasFormationVolumeFactorRCFSCF,
    header: "Gas FVF"
  }
];

const displayedColumns = computed(() => {
  const rows = wellProperties.value?.custom_fluid_properties ?? [];
  if (!rows.length) return [];

  const isPressure = rows[0]?.controlType === VISCOSITY_PRESSURE_TYPE;
  const columnsWithData = allColumns.filter(col =>
    rows.some(
      (row: { [x: string]: string }) =>
        row[col.field] != null && row[col.field] !== ""
    )
  );

  return columnsWithData.map(col => {
    let headerWithUnits = col.header;
    switch (col.field) {
      case FluidPropertyField.controllingValue:
        headerWithUnits += isPressure
          ? ` (${PoundsPerSquareInchAbsolute.label})`
          : ` (${Fahrenheit.label})`;
        break;
      case FluidPropertyField.bubblePointPressurePSIA:
        headerWithUnits += ` (${PoundsPerSquareInchAbsolute.label})`;
        break;
      case FluidPropertyField.oilFormationVolumeFactorBBLSTB:
      case FluidPropertyField.waterFormationVolumeFactorBBLSTB:
        headerWithUnits += ` (${BarrelPerStockTankBarrel.label})`;
        break;
      case FluidPropertyField.solutionGasOilRatioSCFSTB:
      case FluidPropertyField.solutionGasWaterRatioSCFSTB:
        headerWithUnits += ` (${StandardCubicFootPerStockTankBarrel.label})`;
        break;
      case FluidPropertyField.gasFormationVolumeFactorRCFSCF:
        headerWithUnits += " (RCF/SCF)";
        break;
    }
    return { ...col, header: headerWithUnits };
  });
});

function handleFileSelected(file: File) {
  parseFluidPropertiesEntryRowsCorrelations(file).then(rows => {
    if (rows.length === 0) {
      fileInputKeyCorrelations.value++;
      return;
    }
    wellPropertiesForm.update({
      custom_fluid_properties: rows
    });
  });
}

async function parseFluidPropertiesEntryRowsCorrelations(
  file: File
): Promise<FluidPropertiesRow[]> {
  const buffer = await file.arrayBuffer();
  const workbook = read(buffer);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  const data: (string | number | null)[][] = utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: false,
    defval: null
  });

  const dependentVariableHeader = data[5]?.[2];
  if (
    dependentVariableHeader &&
    dependentVariableHeader.toString().toLowerCase().includes("viscosity")
  ) {
    toaster.add({
      severity: "error",
      summary: "Fluid Properties Upload Error",
      detail: `This sheet must NOT be for "Viscosity". 
             Found: "${dependentVariableHeader}"`
    });
    return [];
  }

  const HeaderSpace = 3;
  const startRow = 7;
  const rows: FluidPropertiesRow[] = [];
  let controlType:
    | typeof VISCOSITY_TEMPERATURE_TYPE
    | typeof VISCOSITY_PRESSURE_TYPE
    | null = null;
  const controllingValueSet = new Set<number>();

  for (let i = startRow; i < data.length; i++) {
    const row = data[i];
    const isRowCompletelyEmpty = row.every(
      cell => cell === null || cell === ""
    );
    if (isRowCompletelyEmpty) {
      continue;
    }

    const rawTemp = row[0];
    const rawPressure = row[1];
    const tempValue = typeof rawTemp === "string" ? rawTemp.trim() : rawTemp;
    const pressureValue =
      typeof rawPressure === "string" ? rawPressure.trim() : rawPressure;

    const hasDependentData = row
      .slice(2)
      .some(cell => cell != null && cell !== "");

    if (hasDependentData && !tempValue && !pressureValue) {
      toaster.add({
        severity: "error",
        summary: "Fluid Properties Upload Error",
        detail: `Row ${i + HeaderSpace}: must have either a Temperature or a Pressure.`
      });
      return [];
    }

    if (tempValue && pressureValue) {
      toaster.add({
        severity: "error",
        summary: "Fluid Properties Upload Error",
        detail: `Row ${i + HeaderSpace}: cannot have both Temperature and Pressure. Provide only one.`
      });
      return [];
    }

    if (!controlType) {
      controlType = tempValue
        ? VISCOSITY_TEMPERATURE_TYPE
        : VISCOSITY_PRESSURE_TYPE;
    } else {
      if (controlType === VISCOSITY_TEMPERATURE_TYPE && !tempValue) {
        toaster.add({
          severity: "error",
          summary: "Fluid Properties Upload Error",
          detail: `Row ${i + HeaderSpace} must have a temperature.`
        });
        return [];
      }
      if (controlType === VISCOSITY_PRESSURE_TYPE && !pressureValue) {
        toaster.add({
          severity: "error",
          summary: "Fluid Properties Upload Error",
          detail: `Row ${i + HeaderSpace} must have a pressure.`
        });
        return [];
      }
    }

    const controllingVal =
      controlType === VISCOSITY_TEMPERATURE_TYPE
        ? Number(tempValue)
        : Number(pressureValue);

    if (controllingValueSet.has(controllingVal)) {
      toaster.add({
        severity: "error",
        summary: "Fluid Properties Upload Error",
        detail: `Row ${i + HeaderSpace} has a duplicate ${controlType} value: ${controllingVal}.`
      });
      return [];
    }
    controllingValueSet.add(controllingVal);

    const bubblePoint = toNumberOrNull(row[2]);
    const oilFVF = toNumberOrNull(row[3]);
    const solutionGasOilRatio = toNumberOrNull(row[4]);
    const waterFVF = toNumberOrNull(row[5]);
    const solutionGasWaterRatio = toNumberOrNull(row[6]);
    const gasFVF = toNumberOrNull(row[7]);

    const parsedRow: FluidPropertiesRow = {
      [FluidPropertyField.controllingValue]: controllingVal,
      [FluidPropertyField.controlType]: controlType,
      [FluidPropertyField.bubblePointPressurePSIA]: bubblePoint,
      [FluidPropertyField.oilFormationVolumeFactorBBLSTB]: oilFVF,
      [FluidPropertyField.solutionGasOilRatioSCFSTB]: solutionGasOilRatio,
      [FluidPropertyField.waterFormationVolumeFactorBBLSTB]: waterFVF,
      [FluidPropertyField.solutionGasWaterRatioSCFSTB]: solutionGasWaterRatio,
      [FluidPropertyField.gasFormationVolumeFactorRCFSCF]: gasFVF
    };
    rows.push(parsedRow);
  }

  if (rows.length === 0) {
    toaster.add({
      severity: "error",
      summary: "Fluid Properties Upload Error",
      detail: "No data rows found in the uploaded file."
    });
    return [];
  }
  return rows;
}

function removeData() {
  wellPropertiesForm.update({ custom_fluid_properties: null });
}

// If any of the custom fluid properties rows have a bubble point pressure, clear the wellProperties.bubble_point_pressure_psia
watch(
  () => wellProperties.value.custom_fluid_properties,
  newValue => {
    if (
      newValue !== null &&
      newValue !== undefined &&
      newValue.some(
        (row: RegressionCoefficientsMap) => row.bubblePointPressurePSIA !== null
      ) &&
      wellProperties.value.bubble_point_pressure_psia
    ) {
      wellPropertiesForm.update({ bubble_point_pressure_psia: null });
    }
  }
);
</script>
